"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from "@mui/material";
import { ArrowRight, Link2, Sparkles, Trophy } from "lucide-react";

import { PageHeader } from "@/components/page-header";
import { CardGrid, CardGridSkeleton, EmptyState } from "@/components/page-states";
import { SourceCard } from "@/components/source-card";
import { createCampaign } from "@/features/campaigns/api";
import { listClips } from "@/features/clips/api";
import type { Clip } from "@/features/clips/types";
import { listMoments } from "@/features/moments/api";
import type { Moment } from "@/features/moments/types";
import { importSourceUrl, listPipelineStatuses, listSources } from "@/features/sources/api";
import { buildReviewGroups, reviewFilters, type ReviewFilter } from "@/features/sources/review-groups";
import type { Source, SourcePipelineStatus } from "@/features/sources/types";

type ComposerMode = "standard" | "whop";

export function GeneratePage() {
  const router = useRouter();
  const [mode, setMode] = useState<ComposerMode>("standard");
  const [reviewFilter, setReviewFilter] = useState<ReviewFilter>("ready");
  const [sourceUrl, setSourceUrl] = useState("");
  const [campaignUrl, setCampaignUrl] = useState("");
  const [sources, setSources] = useState<Source[]>([]);
  const [clips, setClips] = useState<Clip[]>([]);
  const [moments, setMoments] = useState<Moment[]>([]);
  const [pipelineStatuses, setPipelineStatuses] = useState<SourcePipelineStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const [nextSources, nextClips, nextMoments, nextStatuses] = await Promise.all([
        listSources(),
        listClips(),
        listMoments(),
        listPipelineStatuses()
      ]);
      setSources(nextSources);
      setClips(nextClips);
      setMoments(nextMoments);
      setPipelineStatuses(nextStatuses);
      setError(null);
    } catch (caughtError) {
      setError(caughtError instanceof Error ? caughtError.message : "Could not load generations");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timeoutId = window.setTimeout(refresh, 0);
    return () => window.clearTimeout(timeoutId);
  }, [refresh]);

  useEffect(() => {
    if (!pipelineStatuses.some((status) => status.has_active_work)) return;
    const intervalId = window.setInterval(refresh, 3000);
    return () => window.clearInterval(intervalId);
  }, [pipelineStatuses, refresh]);

  const statusBySource = useMemo(
    () => new Map(pipelineStatuses.map((status) => [status.source_id, status])),
    [pipelineStatuses]
  );
  const reviewGroups = useMemo(
    () => buildReviewGroups(sources, moments, clips, statusBySource),
    [clips, moments, sources, statusBySource]
  );
  const visibleReviewSources = reviewGroups[reviewFilter];

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);

    if (mode === "whop") {
      if (!isWhopUrl(campaignUrl)) {
        setError("Paste a valid Whop campaign URL.");
        return;
      }
      setSubmitting(true);
      try {
        const campaign = await createCampaign({
          name: deriveCampaignName(campaignUrl),
          whop_url: campaignUrl,
          content_url: sourceUrl || null
        });
        router.push(`/campaigns/${campaign.id}`);
      } catch (caughtError) {
        setError(caughtError instanceof Error ? caughtError.message : "Could not create campaign");
      } finally {
        setSubmitting(false);
      }
      return;
    }

    setSubmitting(true);
    try {
      const source = await importSourceUrl(sourceUrl);
      setSourceUrl("");
      await refresh();
      router.push(`/sources/${source.id}`);
    } catch (caughtError) {
      setError(caughtError instanceof Error ? caughtError.message : "Could not start generation");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box component="main" sx={{ py: { xs: 4, md: 7 } }}>
      <Container maxWidth="xl">
        <Stack spacing={{ xs: 4, md: 6 }}>
          <PageHeader
            eyebrow="Automatic clip studio"
            title="Turn long-form content into clips worth publishing."
            description="Choose a source, let Atelier do the editing, then review only the strongest finished versions."
          />

          <Paper
            variant="outlined"
            sx={{
              p: { xs: 2, md: 3 },
              overflow: "hidden",
              position: "relative",
              background: (theme) => theme.palette.mode === "dark"
                ? "linear-gradient(145deg, rgba(25,30,43,.96), rgba(14,17,24,.96))"
                : "linear-gradient(145deg, #fff, #f8f7ff)"
            }}
          >
            <Box sx={{ position: "absolute", width: 320, height: 320, borderRadius: "50%", bgcolor: "rgba(139,124,246,.10)", filter: "blur(70px)", right: -80, top: -180 }} />
            <Stack spacing={3} sx={{ position: "relative" }}>
              <ToggleButtonGroup
                exclusive
                value={mode}
                onChange={(_, nextMode: ComposerMode | null) => nextMode && setMode(nextMode)}
                aria-label="Generation mode"
                sx={{
                  alignSelf: "flex-start",
                  p: 0.5,
                  gap: 0.5,
                  border: 1,
                  borderColor: "divider",
                  borderRadius: "999px",
                  bgcolor: "rgba(0,0,0,.10)",
                  "& .MuiToggleButtonGroup-grouped": {
                    m: 0,
                    border: "1px solid transparent !important",
                    borderRadius: "7px !important"
                  },
                  "& .MuiToggleButton-root.Mui-selected": {
                    borderColor: "rgba(139,124,246,.5) !important",
                    boxShadow: "0 2px 8px rgba(0,0,0,.14)"
                  }
                }}
              >
                <ToggleButton value="standard" sx={{ px: { xs: 1.25, sm: 2.25 }, fontSize: { xs: "0.75rem", sm: "0.8125rem" } }}>
                  <Stack direction="row" spacing={1} alignItems="center"><Sparkles size={16} /><span>Standard clips</span></Stack>
                </ToggleButton>
                <ToggleButton value="whop" sx={{ px: { xs: 1.25, sm: 2.25 }, fontSize: { xs: "0.75rem", sm: "0.8125rem" } }}>
                  <Stack direction="row" spacing={1} alignItems="center"><Trophy size={16} /><span>Whop campaign</span></Stack>
                </ToggleButton>
              </ToggleButtonGroup>

              <Stack spacing={0.5}>
                <Typography variant="h5" fontWeight={850}>
                  {mode === "standard" ? "Generate from a source URL" : "Create for a Whop campaign"}
                </Typography>
                <Typography color="text.secondary">
                  {mode === "standard"
                    ? "Paste a YouTube or Google Drive video link. The automatic pipeline handles everything else."
                    : "Add the campaign and source links, then review the example campaign brief before generation."}
                </Typography>
              </Stack>

              <Box component="form" onSubmit={handleSubmit}>
                <Stack spacing={1.5}>
                  {mode === "whop" ? (
                    <TextField
                      required
                      fullWidth
                      type="url"
                      label="Whop campaign URL"
                      value={campaignUrl}
                      onChange={(event) => setCampaignUrl(event.target.value)}
                      placeholder="https://whop.com/..."
                      slotProps={{ input: { startAdornment: <Link2 size={18} style={{ marginRight: 10, opacity: 0.65 }} /> } }}
                    />
                  ) : null}
                  <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                    <TextField
                      required
                      fullWidth
                      type="url"
                      label={mode === "whop" ? "Content source URL" : "Video URL"}
                      value={sourceUrl}
                      onChange={(event) => setSourceUrl(event.target.value)}
                      placeholder={mode === "whop" ? "https://www.youtube.com/watch?v=..." : "YouTube or Google Drive link"}
                      slotProps={{ input: { startAdornment: <Link2 size={18} style={{ marginRight: 10, opacity: 0.65 }} /> } }}
                    />
                    <Button type="submit" size="large" variant="contained" disabled={submitting} endIcon={submitting ? <CircularProgress color="inherit" size={17} /> : <ArrowRight size={18} />} sx={{ minWidth: 210 }}>
                      {submitting ? "Starting" : mode === "standard" ? "Generate clips" : "Review campaign"}
                    </Button>
                  </Stack>
                </Stack>
              </Box>
              {mode === "whop" ? <Alert severity="info">This creates a real campaign. Whop page parsing is still a placeholder — you&apos;ll enter requirements manually on the next screen.</Alert> : null}
              {error ? <Alert severity="warning">{error}</Alert> : null}
            </Stack>
          </Paper>

          <Stack spacing={2.5}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-end">
              <Stack spacing={0.5}>
                <Typography component="h2" variant="h5" fontWeight={850}>Review inbox</Typography>
                <Typography color="text.secondary">Move through finished generations without searching through every source.</Typography>
              </Stack>
              <Button component={Link} href="/sources" endIcon={<ArrowRight size={16} />} sx={{ display: { xs: "none", sm: "inline-flex" } }}>View all</Button>
            </Stack>

            <ToggleButtonGroup
              exclusive
              value={reviewFilter}
              onChange={(_, nextFilter: ReviewFilter | null) => nextFilter && setReviewFilter(nextFilter)}
              aria-label="Review inbox filter"
              sx={{ alignSelf: "flex-start", flexWrap: "wrap", gap: 0.75, "& .MuiToggleButtonGroup-grouped": { border: 1, borderColor: "divider", borderRadius: "8px !important" } }}
            >
              {reviewFilters.map((filter) => <ToggleButton key={filter.value} value={filter.value} sx={{ px: 1.75, py: 0.75 }}><Stack direction="row" spacing={0.75} alignItems="center"><span>{filter.label}</span><Box component="span" sx={{ minWidth: 22, px: 0.65, py: 0.1, borderRadius: 999, bgcolor: "rgba(139,124,246,.14)", fontWeight: 850 }}>{reviewGroups[filter.value].length}</Box></Stack></ToggleButton>)}
            </ToggleButtonGroup>

            {loading ? (
              <CardGridSkeleton count={3} height={370} />
            ) : visibleReviewSources.length ? (
              <CardGrid>
                {visibleReviewSources.map((source) => (
                  <SourceCard
                    key={source.id}
                    source={source}
                    pipeline={statusBySource.get(source.id) ?? null}
                    moments={moments.filter((moment) => moment.source_id === source.id)}
                    clips={clips.filter((clip) => clip.source_id === source.id)}
                  />
                ))}
              </CardGrid>
            ) : (
              <EmptyState
                compact
                title={reviewFilter === "ready" ? "Nothing waiting for review" : `No ${reviewFilters.find((filter) => filter.value === reviewFilter)?.label.toLowerCase()} sources`}
                description={sources.length ? "Choose another inbox filter." : "Paste your first source URL above to start."}
              />
            )}
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}

function isWhopUrl(value: string) {
  try {
    return new URL(value).hostname.toLowerCase().includes("whop");
  } catch {
    return false;
  }
}

function deriveCampaignName(whopUrl: string): string {
  try {
    const lastSegment = new URL(whopUrl).pathname.split("/").filter(Boolean).at(-1);
    if (!lastSegment) return "New Whop campaign";
    return lastSegment.replace(/[-_]+/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
  } catch {
    return "New Whop campaign";
  }
}
